import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PushNoteToCmsModal } from '@/components/push-to-cms/PushNoteToCmsModal';
import type { ExternalNoteResolutionSchema } from '@/services/types/external-note-types';

export interface PromptPushNoteRequest {
  matterId: string;
  rawContent: string;
  triggerType: 'staff_note' | 'task_complete' | 'standalone';
  cmsResolution: ExternalNoteResolutionSchema;
  taskTitle?: string;
  matterName?: string;
  clientName?: string;
  sourceType?: string;
  sourceId?: string;
}

interface PushNoteToCmsContextValue {
  /**
   * Show the push-note-to-CMS modal and return a Promise that resolves
   * when the user pushes, skips, or cancels.
   */
  promptPushNote: (request: PromptPushNoteRequest) => Promise<void>;
}

const PushNoteToCmsContext = createContext<PushNoteToCmsContextValue | null>(
  null,
);

export const PushNoteToCmsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [request, setRequest] = useState<PromptPushNoteRequest | null>(null);
  const resolveRef = useRef<(() => void) | null>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.();
    resolveRef.current = null;
  }, []);

  const promptPushNote = useCallback(
    (req: PromptPushNoteRequest): Promise<void> => {
      const canCreateExternalNote =
        !!req.cmsResolution.cms_type &&
        !!req.cmsResolution.config &&
        req.cmsResolution.config.supported_action_types.includes('CREATE');

      if (!canCreateExternalNote) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        resolveRef.current = resolve;
        setRequest(req);
        setIsOpen(true);
      });
    },
    [],
  );

  const value = useMemo(() => ({ promptPushNote }), [promptPushNote]);

  return (
    <PushNoteToCmsContext.Provider value={value}>
      {children}
      {request?.cmsResolution.cms_type && (
        <PushNoteToCmsModal
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) handleClose();
          }}
          matterId={request.matterId}
          rawContent={request.rawContent}
          triggerType={request.triggerType}
          cmsResolution={request.cmsResolution}
          taskTitle={request.taskTitle}
          matterName={request.matterName}
          clientName={request.clientName}
          sourceType={request.sourceType}
          sourceId={request.sourceId}
          showSkip
          onSkip={handleClose}
          onSuccess={handleClose}
        />
      )}
    </PushNoteToCmsContext.Provider>
  );
};

export const usePromptPushNote = (): PushNoteToCmsContextValue => {
  const context = useContext(PushNoteToCmsContext);
  if (!context) {
    throw new Error(
      'usePromptPushNote must be used within a PushNoteToCmsProvider',
    );
  }
  return context;
};
